import * as stylex from '@stylexjs/stylex'
import { useState } from 'react'

import Button from './Button'
import Icon from './Icon'
import { findPerson, formatMoney, v1API, type Balance, type Overview, type SettlementSuggestion } from '../lib/ledger'

const styles = stylex.create({
  wrap: { display: 'flex', flexDirection: 'column', rowGap: '12px', borderBottomWidth: 1, borderColor: '#edf0eb', paddingBottom: '16px', marginBottom: '4px' },
  header: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '10px' },
  title: { fontSize: '13px', fontWeight: 650, color: '#3d5542' },
  copy: { marginTop: '2px', fontSize: '11px', color: '#7b857d' },
  list: { display: 'flex', flexDirection: 'column', rowGap: '8px' },
  row: { display: 'flex', alignItems: 'center', gap: '10px', borderRadius: '0.75rem', backgroundColor: '#f8faf7', padding: '10px 12px' },
  route: { minWidth: 0, flex: '1 1 0%', fontSize: '12px', color: '#526157' },
  amount: { whiteSpace: 'nowrap', fontSize: '12px', fontWeight: 650, color: '#315c3d' },
  error: { borderRadius: '0.65rem', backgroundColor: '#fff3ed', padding: '9px 10px', color: '#9b491e', fontSize: '11px' },
  empty: { borderRadius: '0.65rem', backgroundColor: '#f5f8f4', padding: '10px', color: '#6c796f', fontSize: '11px' },
})

type Props = {
  data: Overview
  groupID: string
  onSettle: (balance: Balance) => void
}

export default function SettlementSuggestions({ data, groupID, onSettle }: Props) {
  const [suggestions, setSuggestions] = useState<SettlementSuggestion[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function loadSuggestions() {
    if (loading) return
    setLoading(true)
    setError('')
    try {
      setSuggestions(await v1API.getGroupSettlementSuggestions(groupID))
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Could not calculate settlement suggestions.')
    } finally {
      setLoading(false)
    }
  }

  function personName(personID: string) {
    if (personID === data.currentUser.id) return 'You'
    return findPerson(data, personID)?.name ?? 'Someone'
  }

  function settleBalance(suggestion: SettlementSuggestion): Balance | null {
    if (suggestion.toPersonId === data.currentUser.id) {
      return { personId: suggestion.fromPersonId, amountCents: suggestion.amountCents, currency: suggestion.currency }
    }
    if (suggestion.fromPersonId === data.currentUser.id) {
      return { personId: suggestion.toPersonId, amountCents: -suggestion.amountCents, currency: suggestion.currency }
    }
    return null
  }

  return <div {...stylex.props(styles.wrap)}>
    <div {...stylex.props(styles.header)}>
      <div><p {...stylex.props(styles.title)}>Smart settlement</p><p {...stylex.props(styles.copy)}>Reduce the group to the fewest practical payments while keeping each person's final balance the same.</p></div>
      <Button disabled={loading} onClick={() => void loadSuggestions()} size="sm" variant="secondary"><Icon name="settle" />{loading ? 'Calculating…' : suggestions ? 'Recalculate' : 'Simplify debts'}</Button>
    </div>
    {error && <p {...stylex.props(styles.error)} role="alert">{error}</p>}
    {suggestions && suggestions.length === 0 && <p {...stylex.props(styles.empty)}>Everyone is already settled up in this group.</p>}
    {suggestions && suggestions.length > 0 && <div {...stylex.props(styles.list)}>{suggestions.map((suggestion, index) => {
      const balance = settleBalance(suggestion)
      return <div {...stylex.props(styles.row)} key={`${suggestion.fromPersonId}-${suggestion.toPersonId}-${suggestion.currency}-${index}`}>
        <p {...stylex.props(styles.route)}><strong>{personName(suggestion.fromPersonId)}</strong> → <strong>{personName(suggestion.toPersonId)}</strong></p>
        <p {...stylex.props(styles.amount)}>{formatMoney(suggestion.amountCents, suggestion.currency)}</p>
        {balance && <Button onClick={() => onSettle(balance)} size="sm">Settle</Button>}
      </div>
    })}</div>}
  </div>
}
